import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/**
 * Brand hero gradient — cyan → navy. Use ONLY for marketing/auth hero
 * surfaces. Chrome and controls remain flat per the styling spec.
 */
export const brandHeroGradient = (theme: Theme): string =>
  `linear-gradient(135deg, ${theme.palette.brand.cyan} 0%, ${theme.palette.brand.navy} 100%)`;

/** Card-at-rest shadow. Centralizes the value referenced by MuiCard + custom cards. */
export const cardRestShadow = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? '0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.05)'
    : '0 1px 2px rgba(0,0,0,.3), 0 4px 16px rgba(0,0,0,.25)';

/** Card-on-hover shadow. */
export const cardHoverShadow = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? '0 2px 6px rgba(15,23,42,.05), 0 18px 40px rgba(15,23,42,.10)'
    : '0 2px 6px rgba(0,0,0,.4), 0 20px 48px rgba(0,0,0,.4)';

/** Subtle panel/tile shadow — one layer lighter than card. */
export const panelShadow = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? '0 1px 2px rgba(15,23,42,.04)'
    : '0 1px 2px rgba(0,0,0,.3)';

/**
 * Crumb-mini style — small uppercase line above a page h1, with a
 * leading cyan dot. Used on Content Library, Tag Manager, etc.
 */
export const crumbMiniSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: theme.palette.brand.cyan,
  },
});

/** Lede paragraph beneath an h1. */
export const ledeSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: '13.5px',
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
  maxWidth: '720px',
  marginTop: '8px',
  marginBottom: '22px',
});

/**
 * Icon-circle treatment used on stat tiles, info-card headers, etc.
 * Pass through MUI `Box` sx prop, then place an icon inside.
 */
export const brandIconCircleSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: theme.palette.mode === 'light'
    ? 'rgba(34,181,220,.12)'
    : 'rgba(34,181,220,.16)',
  color: theme.palette.mode === 'light'
    ? theme.palette.brand.teal
    : theme.palette.brand.cyan,
  '& > svg': { fontSize: 16 },  // size whatever icon is inside, overriding caller's sx
});
